import React, { useEffect, useState } from 'react';
import { EVAdoptionBarriersService, EVBarriersData } from '../../services/evAdoptionBarriersService';
import { ChartLayout } from './ChartLayout';
import EVBarriersBarChart from './EVBarriersBarChart';
import EVBarriersScatterChart from './EVBarriersScatterChart';
import EVBarriersMetrics from './EVBarriersMetrics';

interface EVAdoptionBarriersChartProps {
  location: string;
}

const EVAdoptionBarriersChart: React.FC<EVAdoptionBarriersChartProps> = ({ location }) => {
  const [data, setData] = useState<EVBarriersData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState('overview');
  const [chartType, setChartType] = useState<'bar' | 'scatter'>('scatter');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await EVAdoptionBarriersService.getData(location);
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load EV adoption barriers data');
      } finally {
        setLoading(false);
      }
    };

    if (location) {
      loadData();
    }
  }, [location]);

  if (loading) {
    return <div className="text-center p-8">Loading EV adoption barriers data...</div>;
  }

  if (error) {
    return <div className="text-center p-8 text-red-600">Error: {error}</div>;
  }

  if (!data) {
    return <div className="text-center p-8">No data available</div>;
  }

  const chartContent = (
    <div>
      {chartType === 'bar' ? (
        <EVBarriersBarChart chartData={data.barChartData} chartOptions={data.barChartOptions} />
      ) : (
        <EVBarriersScatterChart
          chartData={data.scatterChartData}
          chartOptions={data.scatterChartOptions}
        />
      )}
    </div>
  );

  const keyMetrics = <EVBarriersMetrics metrics={data.metrics} quintiles={data.quintiles} />;

  const overviewContent = (
    <div className="space-y-4">
      <p className="text-gray-700">{data.narrative}</p>
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
        <h4 className="font-semibold text-blue-900 mb-2">Key Finding</h4>
        <p className="text-blue-800">
          The correlation coefficient of {data.metrics.correlation.toFixed(3)} indicates that income is{' '}
          {data.metrics.correlation > 0.5
            ? 'a strong predictor'
            : data.metrics.correlation > 0.3
            ? 'a moderate predictor'
            : 'a weak predictor'}{' '}
          of EV adoption rates. The EV adoption gap between the highest and lowest income quintiles is{' '}
          {data.metrics.evGap.toFixed(2)} EVs per 100 people.
        </p>
      </div>
    </div>
  );

  const relevanceContent = (
    <div className="space-y-4">
      <h4 className="font-semibold text-gray-900">Why This Matters</h4>
      <p className="text-gray-700">
        Electric vehicle adoption is critical for reducing transportation emissions, which often represent
        the largest source of greenhouse gases in urban areas. However, if EV adoption remains concentrated
        among higher-income households, the climate benefits will be unevenly distributed, and lower-income
        communities will continue to bear disproportionate impacts from air pollution and climate change.
      </p>
      <h4 className="font-semibold text-gray-900 mt-4">Equity Implications</h4>
      <p className="text-gray-700">
        Economic barriers to EV adoption include higher upfront costs, lack of access to home charging
        infrastructure (particularly for renters), and limited availability of used EVs. Addressing these
        barriers requires targeted policies such as income-based incentives, investments in public charging
        infrastructure in underserved areas, and programs to expand access to affordable EVs.
      </p>
      <h4 className="font-semibold text-gray-900 mt-4">Policy Relevance</h4>
      <p className="text-gray-700">
        Understanding the income-based disparities in EV adoption helps policymakers design equitable
        transportation electrification strategies. Programs like California's Clean Vehicle Assistance
        Program provide higher rebates for low-income buyers, directly addressing the barriers revealed
        in this analysis.
      </p>
    </div>
  );

  const methodologyContent = (
    <div className="space-y-4">
      <h4 className="font-semibold text-gray-900">Data Sources</h4>
      <ul className="list-disc list-inside text-gray-700 space-y-2">
        <li>EV adoption data from <code>ev_by_tract_2023</code> table (DMV/CARB registration data)</li>
        <li>Income data from <code>acs5_income</code> table (U.S. Census Bureau, 2023 ACS 5-year estimates)</li>
        <li>Education and homeownership data from <code>ev_by_tract_2023</code> table</li>
      </ul>

      <h4 className="font-semibold text-gray-900 mt-4">Calculations</h4>
      <ul className="list-disc list-inside text-gray-700 space-y-2">
        <li>
          <strong>Income Quintiles:</strong> Census tracts sorted by median income and divided into five
          equal groups (quintiles)
        </li>
        <li>
          <strong>Correlation:</strong> Pearson correlation coefficient (r) calculated between median
          income and EV adoption rate across all tracts
        </li>
        <li>
          <strong>R² (Coefficient of Determination):</strong> Square of correlation coefficient, representing
          the proportion of variance in EV adoption explained by income
        </li>
        <li>
          <strong>EV Gap:</strong> Difference in average EV adoption (EVs per 100 people) between the
          highest and lowest income quintiles
        </li>
      </ul>

      <h4 className="font-semibold text-gray-900 mt-4">Limitations</h4>
      <ul className="list-disc list-inside text-gray-700 space-y-2">
        <li>Correlation does not imply causation; other factors (education, homeownership) also influence EV adoption</li>
        <li>Data reflects 2023 snapshots; adoption patterns may change over time as EV prices decrease</li>
        <li>Census tract boundaries may not perfectly align with neighborhood-level social and economic patterns</li>
        <li>Does not capture barriers beyond income (e.g., charging access, vehicle preferences)</li>
      </ul>

      <h4 className="font-semibold text-gray-900 mt-4">Interpretation Guide</h4>
      <p className="text-gray-700">
        <strong>Bar Chart:</strong> Shows average EV adoption by income quintile, revealing disparities across income levels.
      </p>
      <p className="text-gray-700">
        <strong>Scatter Plot:</strong> Shows individual census tracts with trend line, illustrating the relationship
        between income and EV adoption at a granular level.
      </p>
    </div>
  );

  return (
    <ChartLayout
      title="EV Adoption Barriers by Income Quintile"
      description="Analyzing income-based disparities in electric vehicle adoption"
      keyMetrics={keyMetrics}
      chartContent={chartContent}
      chartSelector={{
        options: [
          { value: 'scatter', label: 'Scatter Plot' },
          { value: 'bar', label: 'Bar Chart' }
        ],
        value: chartType,
        onChange: (value: string) => setChartType(value as 'bar' | 'scatter')
      }}
      overviewContent={overviewContent}
      relevanceContent={relevanceContent}
      methodologyContent={methodologyContent}
      activeView={activeView}
      onViewChange={setActiveView}
      layout="side-by-side"
      enableComments={true}
      location={location}
    />
  );
};

export default EVAdoptionBarriersChart;
